import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Headphones, Clock, UserCheck, MessageSquare, Phone, Shield } from 'lucide-react'
import SEO from '../../components/SEO'

const ManagedHelpDesk = () => {
  const features = [
    {
      icon: UserCheck,
      title: 'Personal Support Team',
      description: 'Dedicated technology experts who know you, your preferences, and your technology ecosystem.'
    },
    {
      icon: Clock,
      title: 'Available Anytime',
      description: '24/7/365 access to expert support via phone, text, email, or video—however you prefer.'
    },
    {
      icon: MessageSquare,
      title: 'Proactive Assistance',
      description: 'We reach out before problems arise, keeping your technology running smoothly.'
    },
    {
      icon: Phone,
      title: 'Instant Response',
      description: 'Direct access to senior engineers—no menus, no hold times, just immediate help.'
    },
    {
      icon: Shield,
      title: 'Complete Confidentiality',
      description: 'All interactions and information handled with absolute discretion and security.'
    },
    {
      icon: Headphones,
      title: 'White Glove Service',
      description: 'Exceptional service standards with meticulous attention to your needs and preferences.'
    }
  ]

  return (
    <>
      <SEO 
        title="Managed Help Desk - Praetorian Works"
        description="Elite help desk services for HNW and UHNW individuals. Personal support teams, 24/7 availability, and white glove service for all your technology needs."
        keywords="private help desk, VIP tech support, personal technology assistance, luxury IT help desk"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Headphones className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Managed
                <span className="text-primary-500 ml-4">Help Desk</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Your personal technology support team, always available when you need them. Expert assistance 
                delivered with discretion, care, and the white glove service you deserve.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-secondary-800 to-secondary-900 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Support That <span className="text-primary-500">Understands You</span>
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Unlike traditional help desks where you're just a ticket number, our Managed Help Desk provides 
                personalized support from a dedicated team that knows you. They understand your technology setup, 
                your preferences, and your unique requirements.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                Whether you need help with a device, guidance on a purchase, training on new technology, or 
                coordination with vendors—we handle it all with the discretion and excellence you expect.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Experience Personal Support
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Discover the difference that dedicated, white glove help desk service makes.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Get Started
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ManagedHelpDesk
